import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Users, BookOpen, CheckCircle, Clock, XCircle, Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";

const roleLabel = { mahasiswa: "Mahasiswa", dosen: "Dosen", admin: "Admin" };
const roleBadge = {
  mahasiswa: "bg-blue-100 text-blue-700",
  dosen: "bg-green-100 text-green-700",
  admin: "bg-purple-100 text-purple-700",
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-[#0F172A] font-[Outfit]">{title}</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-[#0F172A]"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function UserForm({ initial = {}, dosenList = [], onSubmit, loading, error }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    email: initial.email || "",
    password: "",
    role: initial.role || "mahasiswa",
    nim: initial.nim || "",
    nip: initial.nip || "",
    whatsapp: initial.whatsapp || "",
    judul_ta: initial.judul_ta || "",
    pembimbing1_id: initial.pembimbing1_id || "",
    pembimbing2_id: initial.pembimbing2_id || "",
  });
  const [showPass, setShowPass] = useState(false);
  const isEdit = !!initial.id;
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Nama</label>
          <input required type="text" value={form.name} onChange={f("name")} data-testid="user-form-name"
            className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Email</label>
          <input required type="email" value={form.email} onChange={f("email")} data-testid="user-form-email"
            className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">
            Password {isEdit && <span className="text-[#475569] normal-case tracking-normal font-normal">(kosongkan jika tidak diubah)</span>}
          </label>
          <div className="relative">
            <input type={showPass ? "text" : "password"} value={form.password} onChange={f("password")} data-testid="user-form-password"
              required={!isEdit} placeholder={isEdit ? "••••••••" : "Password baru"}
              className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147] pr-9" />
            <button type="button" onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569]">
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Role</label>
          <select value={form.role} onChange={f("role")} disabled={isEdit} data-testid="user-form-role"
            className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#002147] disabled:bg-slate-50">
            <option value="mahasiswa">Mahasiswa</option>
            <option value="dosen">Dosen</option>
          </select>
        </div>

        {form.role === "mahasiswa" && (
          <>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">NIM</label>
              <input type="text" value={form.nim} onChange={f("nim")} data-testid="user-form-nim"
                className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147]" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Judul TA</label>
              <input type="text" value={form.judul_ta} onChange={f("judul_ta")} data-testid="user-form-judul"
                className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147]" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Pembimbing 1</label>
              <select value={form.pembimbing1_id} onChange={f("pembimbing1_id")} data-testid="user-form-p1"
                className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#002147]">
                <option value="">Pilih Pembimbing 1</option>
                {dosenList.filter(d => d.id !== form.pembimbing2_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Pembimbing 2</label>
              <select value={form.pembimbing2_id} onChange={f("pembimbing2_id")} data-testid="user-form-p2"
                className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#002147]">
                <option value="">Pilih Pembimbing 2</option>
                {dosenList.filter(d => d.id !== form.pembimbing1_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </>
        )}

        {form.role === "dosen" && (
          <div className="col-span-2">
            <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">NIP</label>
            <input type="text" value={form.nip} onChange={f("nip")} data-testid="user-form-nip"
              className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147]" />
          </div>
        )}

        <div className="col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-1.5">Nomor WhatsApp</label>
          <input type="tel" value={form.whatsapp} onChange={f("whatsapp")} data-testid="user-form-whatsapp"
            placeholder="cth: 081234567890"
            className="w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002147]" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-600">{error}</div>}

      <button type="submit" disabled={loading} data-testid="user-form-submit"
        className="w-full bg-[#002147] hover:bg-[#001833] disabled:bg-slate-400 text-white py-2.5 rounded-sm font-semibold text-sm transition-all mt-1">
        {loading ? "Menyimpan..." : (isEdit ? "Simpan Perubahan" : "Buat User")}
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [dosenList, setDosenList] = useState([]);
  const [bimbingan, setBimbingan] = useState([]);
  const [stats, setStats] = useState({});
  const [modal, setModal] = useState(null); // null | "create" | {type: "edit", user} | {type: "delete", user}
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [usersRes, statsRes, bRes, dosenRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/bimbingan`, { withCredentials: true }),
        axios.get(`${API}/users/dosen`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setBimbingan(bRes.data);
      setDosenList(dosenRes.data);
    } catch (_) {}
  };

  const handleCreate = async (form) => {
    setFormLoading(true); setFormError("");
    try {
      if (!form.password) { setFormError("Password wajib diisi"); setFormLoading(false); return; }
      await axios.post(`${API}/users`, form, { withCredentials: true });
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Gagal membuat user");
    } finally { setFormLoading(false); }
  };

  const handleEdit = async (form) => {
    setFormLoading(true); setFormError("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/users/${modal.user.id}`, payload, { withCredentials: true });
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Gagal memperbarui user");
    } finally { setFormLoading(false); }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    try {
      await axios.delete(`${API}/users/${modal.user.id}`, { withCredentials: true });
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Gagal menghapus user");
    } finally { setFormLoading(false); }
  };

  const handleBackupFile = async () => {
  try {
    const token = localStorage.getItem("token");

      await axios.post(
        `${API}/admin/backup-file`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      alert("Backup berhasil disimpan di server");

    } catch (err) {
      console.error(err);

      alert(
        err?.response?.data?.detail ||
        "Gagal membuat backup"
      );
    }
  };
  // const handleBackupDrive = async () => {
  // try {
  //   const token = localStorage.getItem("token");
  //   await axios.post(
  //     `${API}/admin/backup-drive`,
  //     {},
  //     {
  //       headers: {
  //         Authorization: `Bearer ${token}`
  //       }
  //     }
  //   );
  //   alert("Backup berhasil diupload ke Google Drive");
  // } catch (err) {
  //   console.error(err);
  //   alert("Gagal backup ke Google Drive");
  // }
  // };

  const filteredUsers = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex font-[Manrope]">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="ml-64 flex-1 p-8">
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Admin Dashboard</h1>
              <p className="text-[#475569] mt-1">Monitoring keseluruhan sistem</p>
              <div className="mt-4">
                <button onClick={handleBackupFile}
                  className="bg-[#002147] hover:bg-[#001833] text-white px-4 py-2 rounded-sm text-sm font-semibold transition-all"
                   >
                      Backup Database ke server
                </button>
                {/* <button 
                  onClick={async () => {
                    const token = localStorage.getItem("token");
                    const res = await axios.get(
                        `${API}/sheets/connect`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`
                          }
                        }
                    );

                      window.location.href = res.data.url;

                    }}
                      className="bg-green-600 text-white px-4 py-2 rounded"
                      >
                      Connect Google
                </button> */}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Mahasiswa", value: stats.total_mahasiswa || 0, icon: <Users size={20} className="text-blue-500" />, color: "blue" },
                { label: "Total Dosen", value: stats.total_dosen || 0, icon: <Users size={20} className="text-green-500" />, color: "green" },
                // { label: "Total Bimbingan", value: stats.total_bimbingan || 0, icon: <BookOpen size={20} className="text-purple-500" />, color: "purple" },
                { label: "Siap Ujian", value: stats.siap_ujian || 0, icon: <CheckCircle size={20} className="text-emerald-500" />, color: "emerald" },
              ].map(s => (
                <div key={s.label} data-testid={`stat-${s.label}`} className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">{s.icon}<p className="text-xs uppercase tracking-widest text-[#475569]">{s.label}</p></div>
                  <p className="text-3xl font-bold text-[#002147] font-[Outfit]">{s.value}</p>
                </div>
              ))}
            </div>

            {/* <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Disetujui", value: stats.approved || 0, Icon: CheckCircle, cls: "text-green-500 bg-green-50" },
                { label: "Menunggu", value: stats.pending || 0, Icon: Clock, cls: "text-yellow-500 bg-yellow-50" },
                { label: "Ditolak", value: stats.rejected || 0, Icon: XCircle, cls: "text-red-500 bg-red-50" },
              ].map(s => (
                <div key={s.label} className={`border border-slate-200 rounded-sm p-5 shadow-sm ${s.cls}`}>
                  <s.Icon size={24} className="mb-2" />
                  <p className="text-2xl font-bold font-[Outfit]">{s.value}</p>
                  <p className="text-xs uppercase tracking-widest mt-1 text-[#475569]">Bimbingan {s.label}</p>
                </div>
              ))}
            </div> */}
          </div>
        )}

        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Kelola Pengguna</h1>
                <p className="text-[#475569] mt-1">{filteredUsers.length} pengguna</p>
              </div>
              <button onClick={() => { setModal("create"); setFormError(""); }} data-testid="create-user-btn"
                className="flex items-center gap-2 bg-[#002147] hover:bg-[#001833] text-white px-4 py-2.5 rounded-sm text-sm font-semibold transition-all active:translate-y-px">
                <Plus size={16} />
                Tambah User
              </button>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama atau email..."
                data-testid="search-users"
                className="flex-1 border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]" />
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                className="border border-slate-200 rounded-sm px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-[#002147]">
                <option value="all">Semua Role</option>
                <option value="mahasiswa">Mahasiswa</option>
                <option value="dosen">Dosen</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="bg-[#002147] text-white text-xs uppercase tracking-widest">
                    <th className="px-4 py-3 text-left font-bold">Nama</th>
                    <th className="px-4 py-3 text-left font-bold">Email</th>
                    <th className="px-4 py-3 text-left font-bold">Role</th>
                    <th className="px-4 py-3 text-left font-bold">Info</th>
                    <th className="px-4 py-3 text-left font-bold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-[#475569] text-sm">Tidak ada data pengguna</td></tr>
                  ) : filteredUsers.map(u => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-[#0F172A] text-sm">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-[#475569]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-sm text-xs font-semibold ${roleBadge[u.role]}`}>
                          {roleLabel[u.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#475569]">
                        {u.nim && <span>NIM: {u.nim}</span>}
                        {u.nip && <span>NIP: {u.nip}</span>}
                        {u.whatsapp && <span className="block mt-0.5">WA: {u.whatsapp}</span>}
                      </td>
                      <td className="px-4 py-3">
                        {u.role !== "admin" && (
                          <div className="flex gap-2">
                            <button onClick={() => { setModal({ type: "edit", user: u }); setFormError(""); }} data-testid={`edit-user-${u.id}`}
                              className="p-1.5 text-[#475569] hover:text-[#002147] hover:bg-slate-100 rounded-sm transition-all">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => setModal({ type: "delete", user: u })} data-testid={`delete-user-${u.id}`}
                              className="p-1.5 text-[#475569] hover:text-red-600 hover:bg-red-50 rounded-sm transition-all">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* {activeTab === "bimbingan" && ( */}
        {false && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Semua Bimbingan</h1>
              <p className="text-[#475569] mt-1">{bimbingan.length} catatan bimbingan</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#002147] text-white text-xs uppercase tracking-widest">
                      <th className="px-4 py-3 text-left font-bold">No</th>
                      <th className="px-4 py-3 text-left font-bold">Mahasiswa</th>
                      <th className="px-4 py-3 text-left font-bold">Dosen</th>
                      <th className="px-4 py-3 text-left font-bold">Tanggal</th>
                      <th className="px-4 py-3 text-left font-bold">Topik</th>
                      <th className="px-4 py-3 text-left font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bimbingan.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-[#475569] text-sm">Belum ada data bimbingan</td></tr>
                    ) : bimbingan.map((b, i) => (
                      <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-[#475569]">{i + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{b.mahasiswa_name || "-"} <span className="text-[#475569] font-normal">({b.mahasiswa_nim || "-"})</span></td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{b.dosen_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{b.tanggal}</td>
                        <td className="px-4 py-3 text-sm text-[#0F172A] max-w-xs truncate">{b.topik}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-sm text-xs font-semibold ${{ pending: "bg-yellow-100 text-yellow-700 border border-yellow-200", approved: "bg-green-100 text-green-700 border border-green-200", rejected: "bg-red-100 text-red-700 border border-red-200" }[b.status]}`}>
                            {{ pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" }[b.status]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {modal === "create" && (
        <Modal title="Tambah Pengguna Baru" onClose={() => setModal(null)}>
          <UserForm dosenList={dosenList} onSubmit={handleCreate} loading={formLoading} error={formError} />
        </Modal>
      )}
      {modal?.type === "edit" && (
        <Modal title="Edit Pengguna" onClose={() => setModal(null)}>
          <UserForm initial={modal.user} dosenList={dosenList} onSubmit={handleEdit} loading={formLoading} error={formError} />
        </Modal>
      )}
      {modal?.type === "delete" && (
        <Modal title="Hapus Pengguna" onClose={() => setModal(null)}>
          <p className="text-[#475569] text-sm mb-4">
            Apakah Anda yakin ingin menghapus akun <strong>{modal.user.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </p>
          {formError && <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-600 mb-3">{formError}</div>}
          <div className="flex gap-3">
            <button onClick={() => setModal(null)}
              className="flex-1 border border-slate-200 text-[#475569] hover:bg-slate-50 py-2.5 rounded-sm font-semibold text-sm transition-all">
              Batal
            </button>
            <button onClick={handleDelete} disabled={formLoading} data-testid="confirm-delete-btn"
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white py-2.5 rounded-sm font-semibold text-sm transition-all">
              {formLoading ? "Menghapus..." : "Ya, Hapus"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
